import { useState, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { apiClient } from '../../utils/api';

// Import fabric for side effects
import 'fabric';

interface DesignData {
  designId: string;
  productId: string;
  variantId?: string;
  designData: object;
  previewUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface SaveDesignResponse {
  success: boolean;
  designId: string;
  previewUrl?: string;
}

interface LoadDesignResponse {
  success: boolean;
  design: DesignData;
}

export const useDesign = () => {
  const { canvas } = useCanvas();
  const { productId, variantId, designId, setDesignId, setLoading, setStatus } = useDesignerStore();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate preview image from canvas
  const generatePreview = useCallback(async (): Promise<string | null> => {
    if (!canvas) return null;

    try {
      // Temporarily hide controls for clean export
      const activeObject = canvas.getActiveObject();
      canvas.discardActiveObject();
      canvas.renderAll();

      // Export as data URL
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5, // Smaller preview
      });

      // Restore selection
      if (activeObject) {
        canvas.setActiveObject(activeObject);
        canvas.renderAll();
      }

      return dataUrl;
    } catch (err) {
      return null;
    }
  }, [canvas]);

  // Save design to server
  const saveDesign = useCallback(async (): Promise<string | null> => {
    if (!canvas || !productId) {
      setError('Canvas or product ID not available');
      return null;
    }

    setIsSaving(true);
    setError(null);
    setLoading(true, 'Saving design...');

    try {
      // Get canvas JSON data
      const designData = canvas.toJSON([
        'id',
        'isGuide',
        'isMockup',
        'isBaseImage',
        'isMaskOverlay',
        'uploadedUrl',
        'serverUrl',
        'laserConverted',
        'convertedFrom',
      ]);

      // Generate preview
      const previewDataUrl = await generatePreview();

      const payload = {
        productId,
        variantId,
        designId, // Will be null for new designs
        designData,
        preview: previewDataUrl,
      };

      const response = await apiClient.post<SaveDesignResponse>('/api/design/save', payload);

      if (response.success && response.data) {
        const newDesignId = response.data.designId;
        setDesignId(newDesignId);
        setStatus('Design saved');

        // Update URL without reload
        const url = new URL(window.location.href);
        url.searchParams.set('designId', newDesignId);
        window.history.replaceState({}, '', url.toString());

        return newDesignId;
      } else {
        throw new Error(response.error || 'Failed to save design');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save design';
      setError(message);
      setStatus('Save failed');
      return null;
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  }, [canvas, productId, variantId, designId, setDesignId, setLoading, setStatus, generatePreview]);

  // Load design from server
  const loadDesign = useCallback(async (loadDesignId: string): Promise<boolean> => {
    if (!canvas) {
      setError('Canvas not available');
      return false;
    }

    setIsLoading(true);
    setError(null);
    setLoading(true, 'Loading design...');

    try {
      const response = await apiClient.get<LoadDesignResponse>(`/api/designs/${loadDesignId}`);

      if (response.success && response.data?.design) {
        const { designData } = response.data.design;

        // Load design into canvas
        await new Promise<void>((resolve) => {
          canvas.loadFromJSON(designData, () => {
            canvas.renderAll();

            // Re-apply system object protections
            canvas.getObjects().forEach((obj) => {
              if (obj.isGuide || obj.isMockup || obj.isBaseImage || obj.isMaskOverlay) {
                obj.set({
                  selectable: false,
                  evented: false,
                });
              }
            });

            resolve();
          });
        });

        setDesignId(loadDesignId);
        setStatus('Design loaded');
        return true;
      } else {
        throw new Error(response.error || 'Failed to load design');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load design';
      setError(message);
      setStatus('Load failed');
      return false;
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [canvas, setDesignId, setLoading, setStatus]);

  // Create a shareable link
  const createShareLink = useCallback(async (): Promise<string | null> => {
    // First save the design if not already saved
    let currentDesignId = designId;

    if (!currentDesignId) {
      currentDesignId = await saveDesign();
      if (!currentDesignId) return null;
    }

    // Generate share URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/design/${productId}?designId=${currentDesignId}`;

    return shareUrl;
  }, [designId, productId, saveDesign]);

  return {
    saveDesign,
    loadDesign,
    createShareLink,
    generatePreview,
    isSaving,
    isLoading,
    error,
  };
};

export default useDesign;
